import { useCallback, useRef, useState } from 'react';
import { Camera, Loader2, Trash2, Upload } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { updatePackage } from '@/lib/api';

interface Props {
  packageId: string;
  photoUrls: string[];
  onChange: (urls: string[]) => void;
}

const BUCKET = 'gift_package_photos';

/**
 * PhotoUpload — multi-file upload met camera-capture voor Mark.
 * - Op telefoon opent de camera direct via capture="environment".
 * - Op desktop wordt het een normale file picker.
 * - URLs worden direct in gift_packages.photo_urls weggeschreven.
 */
export function PhotoUpload({ packageId, photoUrls, onChange }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setError(null);
      setUploading(true);
      try {
        const newUrls: string[] = [];
        for (const file of Array.from(files)) {
          // Bestandsnaam: timestamp + random + originele extensie (default jpg)
          const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
          const safeExt = ext.length <= 5 ? ext : 'jpg';
          const fname = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
          const path = `${packageId}/${fname}`;
          const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
            contentType: file.type || 'image/jpeg',
            cacheControl: '3600',
            upsert: false,
          });
          if (upErr) throw upErr;
          const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
          newUrls.push(data.publicUrl);
        }
        const merged = [...photoUrls, ...newUrls];
        await updatePackage(packageId, { photo_urls: merged });
        onChange(merged);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload mislukt');
      } finally {
        setUploading(false);
        if (fileInput.current) fileInput.current.value = '';
        if (cameraInput.current) cameraInput.current.value = '';
      }
    },
    [packageId, photoUrls, onChange]
  );

  async function handleDelete(url: string) {
    setDeletingUrl(url);
    setError(null);
    try {
      // Path = alles na /storage/v1/object/public/{BUCKET}/
      const marker = `/${BUCKET}/`;
      const idx = url.indexOf(marker);
      if (idx >= 0) {
        const path = url.slice(idx + marker.length);
        // Best-effort: storage delete kan falen (bv. oude file weg), maar UI moet door
        await supabase.storage.from(BUCKET).remove([path]);
      }
      const updated = photoUrls.filter((u) => u !== url);
      await updatePackage(packageId, { photo_urls: updated });
      onChange(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeletingUrl(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => cameraInput.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Camera className="h-4 w-4" />
          )}
          Foto maken
        </button>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-md border border-bmade-300 bg-white px-3 py-2 text-sm font-medium text-bmade-700 hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:bg-bmade-900 dark:text-bmade-200"
        >
          <Upload className="h-4 w-4" />
          Uit galerij kiezen
        </button>
      </div>

      {/* Verborgen inputs: één met camera-capture, één zonder */}
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {photoUrls.length === 0 ? (
        <p className="text-xs text-bmade-500">
          Nog geen foto's. Maak foto's van het samengestelde pakket — Mark fotografeert vaak vanuit
          meerdere hoeken (boven, schuin, open doos).
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {photoUrls.map((url) => (
            <div
              key={url}
              className="group relative overflow-hidden rounded-lg border border-bmade-200 bg-bmade-50 dark:border-bmade-800 dark:bg-bmade-900"
            >
              <img
                src={url}
                alt="Pakket foto"
                className="aspect-square w-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => void handleDelete(url)}
                disabled={deletingUrl === url}
                className="absolute right-1 top-1 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100 disabled:opacity-100"
                title="Verwijder foto"
              >
                {deletingUrl === url ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                Verwijderen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
